import React, { useEffect } from 'react';
import { Player } from '../../types/game';
import { getAvatarUrl } from '../../utils/avatar';
import { createMassiveConfetti } from '../../utils/confetti';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../ui/Card';
import { Text } from '../ui/Text';
import { Button } from '../ui/Button';

interface PodiumScreenProps {
  winners: Player[];
  onPlayAgain: () => void;
}

const PodiumScreen: React.FC<PodiumScreenProps> = ({ winners, onPlayAgain }) => {
  const { theme } = useTheme();
  useEffect(() => {
    createMassiveConfetti();
  }, []);

  const medals = ['🥇', '🥈', '🥉'];

  // Theme-based medal colors (using semantic colors from theme)
  const getMedalStyle = (index: number) => {
    const styles = [
      { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#000' }, // Gold
      { background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)', color: '#000' }, // Silver
      { background: 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)', color: '#fff' }, // Bronze
    ];
    return styles[index] || { background: theme.colors.surface, color: theme.colors.onSurface };
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 animate-[fadeIn_0.3s_ease-in]">
      <Card
        variant="default"
        elevation="xl"
        padding="lg"
        className="max-w-4xl w-full"
      >
        <div className="text-center mb-8">
          <Text as="h1" size="4xl" weight="bold" color="primary" align="center" className="mb-4">
            QuizCrunch
          </Text>
          <div className="text-6xl mb-4">🎉</div>
          <Text as="h2" size="4xl" weight="bold" color="accent" align="center" className="mb-6">
            🏆 Champions 🏆
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {winners.slice(0, 3).map((winner, index) => {
            const medalStyle = getMedalStyle(index);
            return (
              <div
                key={winner.id}
                className="rounded-3xl p-6 text-center transform hover:scale-105 transition-transform duration-300 shadow-xl"
                style={{
                  background: medalStyle.background,
                  color: medalStyle.color,
                  border: `4px solid ${theme.colors.outline}`,
                }}
              >
                <div className="text-6xl mb-3">{medals[index]}</div>
                <img
                  src={getAvatarUrl(winner.name, 128)}
                  alt={`${winner.name}'s avatar`}
                  className="w-24 h-24 mx-auto rounded-full mb-3 shadow-lg"
                  style={{
                    border: `4px solid ${theme.colors.outline}`,
                  }}
                />
                <div className="text-2xl font-bold mb-2">{winner.name}</div>
                <div className="text-lg opacity-90">{winner.score} points</div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={onPlayAgain}
            variant="primary"
            size="lg"
            icon="🎮"
          >
            Play Again!
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PodiumScreen;